import { ModalHandler, SKIP_DISTRO_CHECK_PREFIX } from "@lib/ModalHandler";
import { IDistCheckRequestPayload, ModalMessage, TSectionData } from "@lib/types";
import { fetchJsonApi, getDistDataFetcher, getServiceOrderInfoFetcher } from "@lib/util/api";
import {
  QUESTION_ANSWER_SLEEP_TIME_MS,
  getRandomizedString,
  nodeByText,
  randChoice,
  sleep,
  waitForElementToExist,
} from "@lib/util/common";
import _ from "lodash";
import { Accessor } from "solid-js";
import { hasDistDataChanged } from "./cached-dist-checks";
import {
  getQuestionHandler,
  handleBackAwayFromDistroSection,
  handleGoFowardFromMainDistroSection,
  waitForDistroItemPage,
  waitForDistroPageGenerally,
  waitForRegularSectionPage,
  waitForSOMainMenu,
} from "./form_handlers";
import {
  DistributionCheck,
  SurveyInfo,
  clickSurveySection,
  dispatchChangeEvents,
  getDistCheckItems,
  getJobName,
  getSOID,
  getSurveySections,
} from "./form_util";
import {
  DIST_CHECK_CLOROX_RECOVERY_ANSWER_FLOWS,
  DIST_CHECK_PRIMARY_ANSWER_FLOWS,
  DIST_CHECK_SECONDARY_ANSWER_FLOWS,
  GET_PRESET_OPENING_SECTION,
  completeOtherPresets,
  getBackupNoTagOOSPresetFlow,
} from "./presets";

export async function completeSurvey(modalHandler: Accessor<ModalHandler>) {
  modalHandler().clearAllStatus();

  modalHandler().setIsFilling(true);
  modalHandler().addFillStatusMsg("Fetching SO data");

  const soDataResult = await fetchJsonApi(getServiceOrderInfoFetcher(getSOID()));
  if (!soDataResult.ok) {
    modalHandler().addErrorMessages(soDataResult.errors);
    return;
  }

  modalHandler().addFillStatusMsg("Successfully received SO data!");

  const serviceOrderData = soDataResult.data;

  // const serviceOrderData = ServiceOrderRespSchema.parse(await resp.json());

  modalHandler().setStoreData(serviceOrderData.store);
  modalHandler().setClientName(serviceOrderData.client_name);
  modalHandler().populatePersonnelInfoForm(serviceOrderData.store.contacts, serviceOrderData.store.id);

  await completeOpeningSection(modalHandler);
  await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);

  await completeDecisionMakerSection(modalHandler, serviceOrderData.store.contacts.at(0)?.first_name);
  await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);

  await completePayQuestion(modalHandler, serviceOrderData.estimated_time);
  await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);

  // ==========================================================

  const [surveyInfo, outputModalMessages] = await completeAllDistrochecks(modalHandler);

  // ==========================================================

  // const [surveyInfo, outputModalMessages] = await completeDistroSection(modalHandler);
  await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);
  await completeOtherPresets(
    surveyInfo?.total_scanned_products ?? 0,
    serviceOrderData.client_name,
    modalHandler
  );

  modalHandler().updateBarcodeLinkButton(surveyInfo?.distCheckRespData.barcode_sheet_id);

  modalHandler().clearFillStatus();
  if (outputModalMessages === undefined) {
    console.error("outputModalMessages is undefined");
  } else {
    modalHandler().showFinalStatus(outputModalMessages);
  }

  modalHandler().context.setStatusHeader("Completed autofill!");
  modalHandler().setIsFilling(false);
}

async function completeOpeningSection(modalHandler: Accessor<ModalHandler>) {
  const [clicked] = await clickSurveySection(
    /opening question/i,
    modalHandler,
    waitForRegularSectionPage,
    true
  );
  if (!clicked) return;

  modalHandler().addFillStatusMsg("Filling Opening Section...");

  for (const [i, preset] of GET_PRESET_OPENING_SECTION().entries()) {
    if (preset === null) {
      console.warn(`Preset ${i} is null`);
      continue;
    }

    preset.answer();
    await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);
  }

  nodeByText("button.btn", "Save")!.click();
  await waitForSOMainMenu(modalHandler);
}

async function completeAllDistrochecks(
  modalHandler: Accessor<ModalHandler>
): Promise<[SurveyInfo | undefined, ModalMessage[] | undefined]> {
  const distroSections = getSurveySections(/distro|distribution|recovery/i);
  if (distroSections.length === 0) {
    modalHandler().addWarningMsg(`${SKIP_DISTRO_CHECK_PREFIX}: No distribution sections found.`);
    return [undefined, undefined];
  }

  const rawUpcToDistCheckMap: Record<string, DistributionCheck> = {};

  console.info("distroSections:", distroSections);

  // gather all distro sections' items
  for (const section of distroSections) {
    console.info(`  > Gathering items for section: ${section.title}`);

    const distCheckItems = await getDistroItems(modalHandler, section);

    for (const distCheck of distCheckItems) {
      if (distCheck.rawUpc in rawUpcToDistCheckMap) continue;
      rawUpcToDistCheckMap[distCheck.rawUpc] = distCheck;
    }
  }

  console.info("  > rawUpcToDistCheck:", Object.values(rawUpcToDistCheckMap));

  if (distroSections.length === 0) return [undefined, undefined];

  // fetch dist check data from server
  modalHandler().addFillStatusMsg("Fetching distribution check data from server...");

  const distcheckPayload = getDistCheckPayload(getSOID(), Object.values(rawUpcToDistCheckMap), modalHandler);
  const distCheckRespDataResult = await fetchJsonApi(getDistDataFetcher(distcheckPayload));
  if (!distCheckRespDataResult.ok) {
    modalHandler().addErrorMsg("Failed to fetch distribution check data.");
    return [undefined, undefined];
  }

  // const distCheckRespData = DistCheckSchema.parse(await resp.json());
  modalHandler().addFillStatusMsg("Successfully received distribution check data!");
  const distCheckRespData = distCheckRespDataResult.data;

  if (distCheckRespData.barcode_sheet_id === undefined) {
    modalHandler().addWarningMsg("Server returned no distribution data (no barcode sheet ID).");
    nodeByText("button.btn", "Finished with Alerts")!.click();
    await waitForSOMainMenu(modalHandler);
    return [undefined, undefined];
  }

  const surveyInfo = new SurveyInfo(_.cloneDeep(Object.values(rawUpcToDistCheckMap)), distCheckRespData);

  const newOutputModalMessages = modalHandler().getFinalOutputMessages(surveyInfo);

  const [isHasDataChanged, _cachedOutputMessages] = hasDistDataChanged(
    getSOID(),
    distCheckRespData.product_additions,
    newOutputModalMessages
  );

  // complete distro sections
  for (const distroSection of distroSections) {
    if (!isHasDataChanged && distroSection.isComplete) {
      console.warn(`${SKIP_DISTRO_CHECK_PREFIX}: ${distroSection.title} - data not changed`);
      modalHandler().addWarningMsg(`${SKIP_DISTRO_CHECK_PREFIX}: ${distroSection.title} - data not changed`);

      continue;
    }

    await completeDistroSection(modalHandler, distroSection, surveyInfo);

    await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);
  }

  return [surveyInfo, newOutputModalMessages];
}

/** Modifies sectionData.distCheckItemDatalist */
async function getDistroItems(
  modalHandler: Accessor<ModalHandler>,
  sectionData: TSectionData
): Promise<DistributionCheck[]> {
  const result = await handleGoFowardFromMainDistroSection(modalHandler, sectionData);
  if (!result) {
    throw new Error("Could not find Distro while handling page navigation");
  }

  const [distCheckItems, distCheckItemDatalist] = getDistCheckItems();
  sectionData.distCheckItemDatalist = distCheckItemDatalist;

  await handleBackAwayFromDistroSection(modalHandler, sectionData);

  return distCheckItems;
}

async function completeDistroSection(
  modalHandler: Accessor<ModalHandler>,
  sectionData: TSectionData,
  surveyInfo: SurveyInfo
) {
  const distroSectionType = await handleGoFowardFromMainDistroSection(modalHandler, sectionData);
  if (!distroSectionType) throw new Error("Could not find Distro section of any type");

  modalHandler().addFillStatusMsg(`Completing distro section: ${sectionData.title}`);
  console.info(`  > Completing distro section: ${sectionData.title}`);

  const distCheckItemsNotFilled: DistributionCheck[] = [];

  for (const distCheckItemData of sectionData.distCheckItemDatalist) {
    const distCheck = surveyInfo.getDistCheckbyRawUpc(distCheckItemData.rawUpc);
    if (distCheck === undefined) {
      console.warn(`No dist check found in SurveyInfo for item: ${distCheckItemData.rawUpc}`);
      continue;
    }

    await completeSingleItemDistro(modalHandler, distCheckItemData.element, distCheck);

    if (distCheck.flowResultName === null) {
      distCheckItemsNotFilled.push(distCheck);
    }
  }

  if (distCheckItemsNotFilled.length > 0) {
    modalHandler().addWarningMsg("Not all distribution questions were filled:");
    for (const distCheck of distCheckItemsNotFilled) {
      modalHandler().addWarningMsg(` - ${distCheck.rawUpc} -> '${distCheck.productName}'`);
    }
  }

  await handleBackAwayFromDistroSection(modalHandler, sectionData);
}

async function completeSingleItemDistro(
  modalHandler: Accessor<ModalHandler>,
  distroItemElement: HTMLElement,
  distCheck: DistributionCheck
) {
  if (distCheck.flowResultName === null) {
    console.warn(
      `Product name '${distCheck.productName}' with raw upc '${distCheck.rawUpc}' has flowResultName = null. Skipping`
    );
    return;
  }

  console.log(`Completing single item distro: ${distCheck.productName}`);

  distroItemElement.click();
  modalHandler().context.setStatusHeader(
    `Entering item dist check: ${distCheck.productName}. Waiting for page...`
  );
  await waitForDistroItemPage(modalHandler);

  const questionsContainer = document.getElementById("IAQs");
  if (questionsContainer === null) {
    modalHandler().addErrorMsg("Parent container #IAQs not found in distor question.");
    throw new Error("Parent container #IAQs not found.");
  }

  if (questionsContainer.innerText.includes("Item has been resolved!")) {
    // $nodeByText("a.btn", "List")!.click();
    // $nodeByText("button.btn", "Edit Item")!.click();
    nodeByText("button.btn", "Clear and Re-Enter")!.click();
    const dataEntryContainer = await waitForElementToExist(() => document.querySelector("#dataEntry"));
    if (dataEntryContainer === null) {
      modalHandler().addErrorMsg("#dataEntry element not found.");
      throw new Error("#dataEntry element not found.");
    }
    // return;
  }

  // for lavazza wakefern
  let answerFlowsPreset = DIST_CHECK_PRIMARY_ANSWER_FLOWS;
  if (getJobName().toLowerCase().includes("lavazza wakefern")) {
    answerFlowsPreset = DIST_CHECK_SECONDARY_ANSWER_FLOWS;
    getQuestionHandler(
      "radio",
      1,
      "Please confirm that you acknowledge the attached POGS attached below."
    )?.answer("I acknowledge");
  } else if (getJobName().toLowerCase().includes("asrtmt rcvry")) {
    answerFlowsPreset = DIST_CHECK_CLOROX_RECOVERY_ANSWER_FLOWS;
  }

  const answerFlowResult = answerFlowsPreset.answer_flows[distCheck.flowResultName];
  for (const [i, presetFlow] of answerFlowResult.get_preset_flow().entries()) {
    console.log(presetFlow);

    if (presetFlow === null) {
      console.error(`${distCheck.flowResultName}: Failed to resolve the ${i}-th preset in the list`);
      continue;
    }

    const result = presetFlow.answer();
    if (!result) {
      console.warn("Failed to answer question:", presetFlow.questionText);
      getBackupNoTagOOSPresetFlow()?.answer();
      // answer with several possible ways to say insufficient space
      getQuestionHandler("textinput", 6, "Why did the decision maker refuse?")?.answer(
        randChoice([
          "Insufficient space",
          "No space",
          "Lack of space",
          "Not enough space",
          "No room",
          "Not enough room",
          "Insufficient room",
          "Space issues",
          "Room issues",
          "Space constraints",
          "No available space",
          "Lack of available space",
        ])
      );
    }

    await sleep(QUESTION_ANSWER_SLEEP_TIME_MS);
  }

  getQuestionHandler("textinput", 6, "How many units of products did you packout?")?.answer(
    distCheck.unitsPacked?.toString() ?? ""
  );

  getQuestionHandler("textinput", 7, "How many cases of products did you order?")?.answer(
    distCheck.casesOrdered?.toString() ?? ""
  );

  console.log("  >> Waiting for Distro main menu");
  nodeByText("button.btn", "Save Alert")!.click();
  await waitForDistroPageGenerally(modalHandler, "Distro main menu", false);
  // await waitForElementWithTextToExist("h3.ntm", /Required Alerts to Check Out/);
  // await attemptElementClick(2, () => $nodeByText("button.btn", "Save Alert")!);
}

async function completeDecisionMakerSection(
  modalHandler: Accessor<ModalHandler>,
  personnelFirstName: string | undefined
) {
  if (personnelFirstName === undefined || personnelFirstName.trim() === "") {
    console.warn("personnel first name is undefined or empty");
    return;
  }

  const [clicked] = await clickSurveySection(
    /decisi?on maker/i,
    modalHandler,
    waitForRegularSectionPage,
    true
  );
  if (!clicked) return;

  modalHandler().addFillStatusMsg("Attempting to fill decision maker name...");

  getQuestionHandler(
    "textinput",
    1,
    /what is the decision maker's first and last name|please enter first and last name of decision maker/i
  )?.answer(personnelFirstName);

  getQuestionHandler("radio", 2, "WHAT IS THE DECISION MAKER'S TITLE?")?.answer(
    "GROCERY / DAIRY / FROZEN MANAGER"
  );

  getQuestionHandler("radio", 2, "What is the Decision Maker's Title?")?.answer("Grocery Manager");
  getQuestionHandler("textinput", 3, "DECISION MAKER'S EMAIL")?.answer(
    getRandomizedString(["n", ".,/", "a"])
  );
  getQuestionHandler("radio", 3, "Do you know the Decision Maker's email address?")?.answer("NO");

  nodeByText("button.btn", "Save")!.click();
  await waitForSOMainMenu(modalHandler);
}

async function completePayQuestion(
  modalHandler: Accessor<ModalHandler>,
  estimatedTimeHr: number
): Promise<void> {
  const [clicked] = await clickSurveySection(/pay question/i, modalHandler, waitForRegularSectionPage, true);
  if (!clicked) return;

  modalHandler().addFillStatusMsg("Attempting to complete timekeeping...");

  const select = document.getElementById("dataEntry")?.querySelector("select");
  if (!select) {
    modalHandler().addErrorMsg("Time entry Select Dropdown element not found.");
    nodeByText("button.btn", "Save")!.click();
    await waitForSOMainMenu(modalHandler);
    return;
  }

  let previousOption: HTMLOptionElement | null = null;

  for (const option of select.options) {
    const value = Number.parseFloat(option.value);
    if (Number.isNaN(value)) continue;

    if (value > estimatedTimeHr) {
      if (previousOption) {
        select.value = previousOption.value;
        dispatchChangeEvents(select);
      }
      break;
    }

    previousOption = option;
  }

  nodeByText("button.btn", "Save")!.click();
  await waitForSOMainMenu(modalHandler);
}

function getDistCheckPayload(
  soid: number,
  distChecks: DistributionCheck[],
  modalHandler: Accessor<ModalHandler>
): IDistCheckRequestPayload {
  if (distChecks.length === 0) {
    modalHandler().addErrorMsg("No distribution check questions found to generate payload.");
  }
  return {
    soid: soid,
    products: distChecks.map((distcheck) => {
      return {
        raw_upc: distcheck.rawUpc,
        name: distcheck.productName,
      };
    }),
  } satisfies IDistCheckRequestPayload;
}
